import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MusicCue, MusicScene, SoundEffect } from './catalog';
import { MusicEngine } from './musicEngine';

const PREFERENCES_KEY = 'inazuma-manager-audio-v1';

type MusicContextValue = {
  enabled: boolean;
  volume: number;
  effectsEnabled: boolean;
  effectsVolume: number;
  setEnabled(enabled: boolean): void;
  toggle(): void;
  setVolume(volume: number): void;
  setEffectsEnabled(enabled: boolean): void;
  setEffectsVolume(volume: number): void;
  resume(): void;
  setScene(scene: MusicScene): void;
  setFuryActive(active: boolean): void;
  playCue(cue: MusicCue): void;
  finishCue(): void;
  playEffect(effect: SoundEffect): void;
};

function readPreferences() {
  try {
    const stored = JSON.parse(localStorage.getItem(PREFERENCES_KEY) ?? '{}') as Partial<{ enabled: boolean; volume: number; effectsEnabled: boolean; effectsVolume: number }>;
    return {
      enabled: stored.enabled !== false,
      volume: typeof stored.volume === 'number' ? Math.max(0, Math.min(1, stored.volume)) : 0.65,
      effectsEnabled: stored.effectsEnabled !== false,
      effectsVolume: typeof stored.effectsVolume === 'number' ? Math.max(0, Math.min(1, stored.effectsVolume)) : 0.8,
    };
  } catch {
    return { enabled: true, volume: 0.65, effectsEnabled: true, effectsVolume: 0.8 };
  }
}

const MusicContext = createContext<MusicContextValue | null>(null);

export function MusicProvider({ children }: { children: ReactNode }) {
  const [engine] = useState(() => new MusicEngine());
  const [preferences, setPreferences] = useState(readPreferences);

  useEffect(() => {
    engine.setVolume(preferences.volume);
    engine.setEnabled(preferences.enabled);
    engine.setEffectsVolume(preferences.effectsVolume);
    engine.setEffectsEnabled(preferences.effectsEnabled);
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  }, [engine, preferences]);

  useEffect(() => {
    const unlock = () => engine.unlock();
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      engine.destroy();
    };
  }, [engine]);

  // Apply media actions synchronously inside the user's click/keyboard event.
  // Deferring play() to an effect makes browsers treat it as autoplay and can
  // leave music silent after the user explicitly re-enables it.
  const setEnabled = useCallback((enabled: boolean) => {
    engine.setEnabled(enabled);
    setPreferences((current) => ({ ...current, enabled }));
  }, [engine]);
  const toggle = useCallback(() => setPreferences((current) => {
    const enabled = !current.enabled;
    engine.setEnabled(enabled);
    return { ...current, enabled };
  }), [engine]);
  const setVolume = useCallback((volume: number) => {
    engine.setVolume(volume);
    setPreferences((current) => ({ ...current, volume }));
  }, [engine]);
  const setEffectsEnabled = useCallback((effectsEnabled: boolean) => {
    engine.setEffectsEnabled(effectsEnabled);
    setPreferences((current) => ({ ...current, effectsEnabled }));
  }, [engine]);
  const setEffectsVolume = useCallback((effectsVolume: number) => {
    engine.setEffectsVolume(effectsVolume);
    setPreferences((current) => ({ ...current, effectsVolume }));
  }, [engine]);
  const resume = useCallback(() => engine.unlock(), [engine]);
  const setScene = useCallback((scene: MusicScene) => engine.setScene(scene), [engine]);
  const setFuryActive = useCallback((active: boolean) => engine.setFuryActive(active), [engine]);
  const playCue = useCallback((cue: MusicCue) => engine.playCue(cue), [engine]);
  const finishCue = useCallback(() => engine.finishCue(), [engine]);
  const playEffect = useCallback((effect: SoundEffect) => engine.playEffect(effect), [engine]);
  const value = useMemo(() => ({ ...preferences, setEnabled, toggle, setVolume, setEffectsEnabled, setEffectsVolume, resume, setScene, setFuryActive, playCue, finishCue, playEffect }), [finishCue, playCue, playEffect, preferences, resume, setEffectsEnabled, setEffectsVolume, setEnabled, setFuryActive, setScene, setVolume, toggle]);
  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (!context) throw new Error('useMusic must be used inside MusicProvider.');
  return context;
}

export function useMusicScene(scene?: MusicScene) {
  const { setScene } = useMusic();
  useEffect(() => {
    if (scene) setScene(scene);
  }, [scene, setScene]);
}
